import { useCallback, useEffect, useRef, useState } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import Select from "react-select";
import { useSelector, useDispatch } from "react-redux";
import { setFilterField, setVisibleColumns, setSortOrder, setSortBy } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import APIs from "../../Constants/apis";
import axiosInstance from "../../config/axiosApi/axiosApi";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast from "../../components/CustomComponents/toast/toast";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import moment from "moment";
import Pagination from "../../components/CustomComponents/Pagination/pagination";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import "./zReport.css";
import "./../order/ManageOrderFilters.css";

const TABS = {
  TRANSACTIONS: "transactions",
  DAILY: "daily",
  CATEGORY: "category",
};

const cashTypeOptions = [
  { value: "all", label: "All cash types" },
  { value: "petty", label: "Petty" },
  { value: "float", label: "Float" },
];

const floatTypeOptions = [
  { value: "all", label: "All float types" },
  { value: "Opening Balance", label: "Opening Balance" },
  { value: "Deposit", label: "Deposit" },
  { value: "Withdraw", label: "Withdraw" },
];

const sortByOptions = [
  { value: "PCDateTime", label: "Date / time" },
  { value: "Amount", label: "Amount" },
  { value: "ExpenType", label: "Category" },
  { value: "StaffName", label: "Staff" },
  { value: "TillName", label: "Till" },
];

const defaultVisibleColumns = {
  pcDateTime: true,
  location: true,
  cashType: true,
  category: true,
  amount: true,
  staff: true,
  till: true,
  reference: false,
};

const columnDefinitions = {
  pcDateTime: "Date / time",
  location: "Location",
  cashType: "Cash type",
  category: "Category",
  amount: "Amount",
  staff: "Staff",
  till: "Till",
  reference: "Reference",
};

const toValidDate = (v) => {
  if (v == null) return null;
  const d = typeof v === "string" ? new Date(v) : v;
  return d instanceof Date && !isNaN(d.getTime()) ? d : null;
};

const val = (row, camel, pascal) => row[camel] ?? row[pascal];

const PettyFloatCashReport = () => {
  const dispatch = useDispatch();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;

  const pageKey = "pettyFloatCashReport";
  const defaultLocation = { value: "all", label: "All Locations" };
  const defaultCashType = cashTypeOptions[0];
  const defaultPettyHead = { value: "all", label: "All petty heads" };
  const defaultFloatType = floatTypeOptions[0];
  const defaultStaff = { value: "all", label: "All staff" };
  const defaultTill = { value: "all", label: "All tills" };
  const defaultDateRange = [
    moment().startOf("week").toDate(),
    moment().endOf("week").toDate(),
  ];
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = sortByOptions[0];
  const defaultPageSize = { value: "20", label: "20" };

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const location = preferences.location || defaultLocation;
  const cashType = preferences.cashType || defaultCashType;
  const pettyHead = preferences.pettyHead || defaultPettyHead;
  const floatType = preferences.floatType || defaultFloatType;
  const staffFilter = preferences.staffFilter || defaultStaff;
  const tillFilter = preferences.tillFilter || defaultTill;
  const storedDateRange = preferences.dateRange;

  const dateRange = (() => {
    if (!storedDateRange || !Array.isArray(storedDateRange)) return defaultDateRange;
    const start = toValidDate(storedDateRange[0]);
    const end = toValidDate(storedDateRange[1]);
    if (!start && !end) return defaultDateRange;
    return [start || defaultDateRange[0], end || defaultDateRange[1]];
  })();

  const [rawStartDate, rawEndDate] = dateRange;

  const [locations, setLocations] = useState([]);
  const [pettyHeads, setPettyHeads] = useState([]);
  const [staffOptions, setStaffOptions] = useState([defaultStaff]);
  const [tillOptions, setTillOptions] = useState([defaultTill]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [activeTab, setActiveTab] = useState(TABS.TRANSACTIONS);
  const [loading, setLoading] = useState(false);
  const [transactionRows, setTransactionRows] = useState([]);
  const [dailyRows, setDailyRows] = useState([]);
  const [categoryRows, setCategoryRows] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const initRanRef = useRef(false);

  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const themeColor = useThemeColor();
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();

  const currencySymbols = { GBP: "£", USD: "$", EUR: "€" };
  const getCurrencySymbol = (code) => currencySymbols[code] || code;
  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return `${getCurrencySymbol(compCurrency)}0.00`;
    return `${getCurrencySymbol(compCurrency)}${parseFloat(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const parentIdForLocation = isMain && CompID != null ? (typeof CompID === "number" ? CompID : parseInt(CompID, 10)) : null;
  const companyName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";
  const locationOptions =
    locations.length > 0
      ? [{ value: "all", label: "All Locations" }, ...locations]
      : isMain && parentIdForLocation != null && !isNaN(parentIdForLocation)
        ? [{ value: "all", label: "All Locations" }, { value: parentIdForLocation, label: companyName }]
        : [{ value: "all", label: "All Locations" }];

  const pettyHeadOptions = [
    defaultPettyHead,
    ...pettyHeads.map((h) => ({
      value: h.name ?? h.Name ?? "",
      label: h.name ?? h.Name ?? "",
    })).filter((o) => o.value),
  ];

  const dateRangeKey =
    rawStartDate && rawEndDate
      ? `${moment(rawStartDate).format("YYYY-MM-DD")}-${moment(rawEndDate).format("YYYY-MM-DD")}`
      : "";

  const locationValue = location?.value ?? "all";
  const cashTypeValue = cashType?.value ?? "all";
  const pettyHeadValue = pettyHead?.value ?? "all";
  const floatTypeValue = floatType?.value ?? "all";
  const staffFilterValue = staffFilter?.value ?? "all";
  const tillFilterValue = tillFilter?.value ?? "all";
  const sortByValue = sortBy?.value ?? "PCDateTime";
  const sortOrderValue = sortOrder?.value ?? "DESC";
  const isPettyDisabled = cashTypeValue === "float";
  const isFloatTypeDisabled = cashTypeValue === "petty";

  const handleFilterChange = (field, selectedOption) => {
    dispatch(setFilterField({ pageKey, field, value: selectedOption }));
    setPageNumber(1);
  };

  const getLocationFilterValue = () => {
    if (!isMain || locationValue === "all" || locationValue == null) return null;
    const locValue = typeof locationValue === "number" ? locationValue : parseInt(locationValue, 10);
    return !isNaN(locValue) && locValue > 0 ? locValue : null;
  };

  const getIsFloatCashFilter = () => {
    if (cashTypeValue === "petty") return false;
    if (cashTypeValue === "float") return true;
    return null;
  };

  const getExpenTypeFilter = () => {
    if (!isPettyDisabled && pettyHeadValue !== "all") return pettyHeadValue;
    if (!isFloatTypeDisabled && floatTypeValue !== "all") return floatTypeValue;
    return null;
  };

  const buildRequestParams = () => {
    const startDate = dateRangeKey.slice(0, 10);
    const endDate = dateRangeKey.slice(11);
    return {
      CompID,
      StartDate: startDate,
      EndDate: endDate,
      LocationFilter: getLocationFilterValue(),
      IsFloatCashFilter: getIsFloatCashFilter(),
      ExpenTypeFilter: getExpenTypeFilter(),
      StaffFilter: staffFilterValue !== "all" ? staffFilterValue : null,
      TillFilter: tillFilterValue !== "all" ? tillFilterValue : null,
      Search: appliedSearch || null,
    };
  };

  const fetchLocations = useCallback(async () => {
    if (!isMain || !CompID) return;
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, { params: { CompID } });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      const parentId = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
      const compName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";
      const base = [{ value: parentId, label: compName }];
      const branchOpts = arr
        .filter((loc) => {
          const pid = loc.ParentComID ?? loc.parentComID;
          const id = loc.CompID ?? loc.compID;
          const pidNum = pid != null ? (typeof pid === "number" ? pid : parseInt(pid, 10)) : null;
          const idNum = id != null ? (typeof id === "number" ? id : parseInt(id, 10)) : null;
          if (idNum == null || isNaN(idNum)) return false;
          return pidNum === parentId && idNum !== parentId;
        })
        .map((loc) => {
          const id = loc.CompID ?? loc.compID;
          const name = String(loc.CompName ?? loc.compName ?? "").trim() || `Branch ${id ?? ""}`;
          const valNum = typeof id === "number" ? id : parseInt(id, 10);
          return { value: isNaN(valNum) ? null : valNum, label: name };
        })
        .filter((o) => o.value != null);
      setLocations([...base, ...branchOpts]);
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Error loading locations");
    } finally {
      setLoadingLocations(false);
    }
  }, [CompID, isMain, profileData?.compName, profileData?.CompName]);

  const fetchPettyHeads = useCallback(async () => {
    if (!CompID) return;
    try {
      const response = await axiosInstance.get(APIs.GET_PETTY_CASH_HEADS, {
        params: { CompID, Search: "" },
      });
      const data = response.data?.data ?? response.data ?? [];
      setPettyHeads(Array.isArray(data) ? data : []);
    } catch {
      setPettyHeads([]);
    }
  }, [CompID]);

  const fetchFilterOptions = useCallback(async () => {
    if (!CompID || !dateRangeKey) return;
    const startDate = dateRangeKey.slice(0, 10);
    const endDate = dateRangeKey.slice(11);
    if (!startDate || !endDate) return;
    try {
      const response = await axiosInstance.get(APIs.GET_CASH_MOVEMENT_FILTER_OPTIONS, {
        params: {
          CompID,
          StartDate: startDate,
          EndDate: endDate,
          LocationFilter: getLocationFilterValue(),
        },
      });
      const payload = response.data?.data ?? {};
      const staffNames = payload.staffNames ?? payload.StaffNames ?? [];
      const tillNames = payload.tillNames ?? payload.TillNames ?? [];
      setStaffOptions([
        defaultStaff,
        ...(Array.isArray(staffNames) ? staffNames : []).map((s) => ({ value: s, label: s })),
      ]);
      setTillOptions([
        defaultTill,
        ...(Array.isArray(tillNames) ? tillNames : []).map((t) => ({ value: t, label: t })),
      ]);
    } catch {
      setStaffOptions([defaultStaff]);
      setTillOptions([defaultTill]);
    }
  }, [CompID, dateRangeKey, locationValue, isMain]);

  const fetchSummary = useCallback(async () => {
    if (!CompID || !dateRangeKey) return;
    const startDate = dateRangeKey.slice(0, 10);
    const endDate = dateRangeKey.slice(11);
    if (!startDate || !endDate) return;
    try {
      const response = await axiosInstance.get(APIs.GET_CASH_MOVEMENT_SUMMARY, {
        params: buildRequestParams(),
      });
      if (!response.data?.status) {
        showErrorToast(response.data?.message || "Failed to load summary");
        return;
      }
      const payload = response.data.data ?? {};
      setKpi(payload.kpi ?? payload.Kpi ?? null);
      setDailyRows(payload.dailySummary ?? payload.DailySummary ?? []);
      setCategoryRows(payload.categorySummary ?? payload.CategorySummary ?? []);
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to load summary");
    }
  }, [
    CompID,
    dateRangeKey,
    locationValue,
    isMain,
    cashTypeValue,
    pettyHeadValue,
    floatTypeValue,
    staffFilterValue,
    tillFilterValue,
    appliedSearch,
  ]);

  const fetchTransactions = useCallback(async () => {
    if (!CompID || !dateRangeKey) return;
    const startDate = dateRangeKey.slice(0, 10);
    const endDate = dateRangeKey.slice(11);
    if (!startDate || !endDate) {
      showErrorToast("Please select a valid date or date range.");
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.get(APIs.GET_CASH_MOVEMENT_REPORT, {
        params: {
          ...buildRequestParams(),
          SortBy: sortByValue,
          SortOrder: sortOrderValue,
          PageNumber: pageNumber,
          PageSize: pageSize.value,
        },
      });
      if (!response.data?.status) {
        showErrorToast(response.data?.message || "Failed to load transactions");
        setTransactionRows([]);
        setTotalRecords(0);
        setTotalPages(0);
        return;
      }
      const payload = response.data.data ?? {};
      const data = payload.data ?? payload.Data ?? [];
      setTransactionRows(Array.isArray(data) ? data : []);
      setTotalRecords(payload.totalRecords ?? payload.TotalRecords ?? 0);
      setTotalPages(payload.totalPages ?? payload.TotalPages ?? 0);
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to load transactions");
      setTransactionRows([]);
      setTotalRecords(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [
    CompID,
    dateRangeKey,
    locationValue,
    isMain,
    cashTypeValue,
    pettyHeadValue,
    floatTypeValue,
    staffFilterValue,
    tillFilterValue,
    appliedSearch,
    sortByValue,
    sortOrderValue,
    pageNumber,
    pageSize.value,
  ]);

  useEffect(() => {
    if (initRanRef.current) return;
    initRanRef.current = true;
    if (!preferences.location) dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    const needsDefaultDate =
      !storedDateRange ||
      !Array.isArray(storedDateRange) ||
      !toValidDate(storedDateRange[0]) ||
      !toValidDate(storedDateRange[1]);
    if (needsDefaultDate) dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    if (!preferences.sortOrder) dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    if (!preferences.sortBy) dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    }
  }, [dispatch, pageKey]);

  useEffect(() => {
    if (isMain) fetchLocations();
    fetchPettyHeads();
  }, [isMain, fetchLocations, fetchPettyHeads]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    const t = setTimeout(() => setAppliedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (activeTab !== TABS.TRANSACTIONS) return;
    fetchTransactions();
  }, [activeTab, fetchTransactions]);

  const handleDateRangeChange = (dates) => {
    const [startDate, endDate] = Array.isArray(dates) ? dates : [dates, null];
    let effectiveStart = startDate ? (typeof startDate === "string" ? new Date(startDate) : startDate) : null;
    let effectiveEnd = endDate ? (typeof endDate === "string" ? new Date(endDate) : endDate) : null;
    if (effectiveStart && effectiveEnd && effectiveStart > effectiveEnd) {
      [effectiveStart, effectiveEnd] = [effectiveEnd, effectiveStart];
    } else if (effectiveStart && !effectiveEnd) {
      effectiveEnd = moment(effectiveStart).endOf("day").toDate();
      effectiveStart = moment(effectiveStart).startOf("day").toDate();
    }
    dispatch(setFilterField({ pageKey, field: "dateRange", value: [effectiveStart, effectiveEnd] }));
    setPageNumber(1);
  };

  const clearAllFilters = () => {
    dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    dispatch(setFilterField({ pageKey, field: "cashType", value: defaultCashType }));
    dispatch(setFilterField({ pageKey, field: "pettyHead", value: defaultPettyHead }));
    dispatch(setFilterField({ pageKey, field: "floatType", value: defaultFloatType }));
    dispatch(setFilterField({ pageKey, field: "staffFilter", value: defaultStaff }));
    dispatch(setFilterField({ pageKey, field: "tillFilter", value: defaultTill }));
    dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    setSearch("");
    setAppliedSearch("");
    setPageNumber(1);
  };

  const formatDateTime = (dt) => {
    if (!dt) return "-";
    const d = typeof dt === "string" ? new Date(dt) : dt;
    return d instanceof Date && !isNaN(d.getTime()) ? moment(d).format("D MMM YYYY, HH:mm") : "-";
  };

  const formatDate = (dt) => {
    if (!dt) return "-";
    const d = typeof dt === "string" ? new Date(dt) : dt;
    return d instanceof Date && !isNaN(d.getTime()) ? moment(d).format("D MMM YYYY") : "-";
  };

  const kpiData = kpi ?? {};

  const visibleTransactionColCount = [
    visibleColumns.pcDateTime,
    visibleColumns.location && isMain,
    visibleColumns.cashType,
    visibleColumns.category,
    visibleColumns.amount,
    visibleColumns.staff,
    visibleColumns.till,
    visibleColumns.reference,
  ].filter(Boolean).length;

  const activeTabRecordCount =
    activeTab === TABS.TRANSACTIONS
      ? totalRecords
      : activeTab === TABS.DAILY
        ? dailyRows.length
        : categoryRows.length;

  const renderCashTypeBadge = (label) => {
    const text = label ?? "";
    const isFloat = String(text).toLowerCase().includes("float");
    return (
      <span className={`petty-float-cash-badge${isFloat ? " petty-float-cash-badge--float" : " petty-float-cash-badge--petty"}`}>
        {text || "-"}
      </span>
    );
  };

  const tableHeadStyle = {
    position: "sticky",
    top: 0,
    zIndex: 10,
    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
    color: isDarkMode ? "#ffffff" : "#212529",
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#/">Home</a></li>
            <li className="breadcrumb-item"><a href="#/report">Report</a></li>
            <li className="breadcrumb-item active">Petty &amp; Float Cash Report</li>
          </ol>
          <h1 className="page-header mb-0">Petty &amp; Float Cash Report</h1>
        </div>
      </div>

      <div
        className="card mb-4 petty-float-report-filters"
        style={{
          position: "relative",
          zIndex: 10,
          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
          borderRadius: "12px",
          border: "none",
          boxShadow: `0 4px 20px ${themeColor}40`,
        }}
      >
        <div className="card-body px-3 py-3 px-md-4 py-md-4">
          <div className="row g-2 g-lg-3 align-items-center mb-2">
            <div className="col-6 col-lg-4 col-xl-2">
              <div className="filter-control h-100 d-flex align-items-center">
                <DateRangePicker
                  startDate={toValidDate(rawStartDate)}
                  endDate={toValidDate(rawEndDate)}
                  onChange={handleDateRangeChange}
                  placeholder="Select date or date range"
                  className="filter-dropdown w-100"
                  allowSingleDate={true}
                  allowDateRange={true}
                />
              </div>
            </div>
            {isMain && locationOptions.length > 1 && (
              <div className="col-6 col-lg-4 col-xl-2">
                <div className="filter-control h-100 d-flex align-items-center">
                  <Select
                    value={location}
                    onChange={(option) => handleFilterChange("location", option)}
                    options={locationOptions}
                    styles={dropdownStyles}
                    isSearchable={false}
                    isLoading={loadingLocations}
                    placeholder="Location"
                    menuPosition="absolute"
                    className="w-100"
                    classNamePrefix="react-select"
                  />
                </div>
              </div>
            )}
            <div className="col-6 col-lg-4 col-xl-2">
              <div className="filter-control h-100 d-flex align-items-center">
                <Select
                  value={cashType}
                  onChange={(option) => handleFilterChange("cashType", option)}
                  options={cashTypeOptions}
                  styles={dropdownStyles}
                  isSearchable={false}
                  placeholder="Cash type"
                  menuPosition="absolute"
                  className="w-100"
                  classNamePrefix="react-select"
                />
              </div>
            </div>
            <div className="col-6 col-lg-4 col-xl-2">
              <div className="filter-control h-100 d-flex align-items-center">
                <Select
                  value={pettyHead}
                  onChange={(option) => handleFilterChange("pettyHead", option)}
                  options={pettyHeadOptions}
                  styles={dropdownStyles}
                  isSearchable={true}
                  isDisabled={isPettyDisabled}
                  placeholder="Petty head"
                  menuPosition="absolute"
                  className="w-100"
                  classNamePrefix="react-select"
                />
              </div>
            </div>
            <div className="col-6 col-lg-4 col-xl-2">
              <div className="filter-control h-100 d-flex align-items-center">
                <Select
                  value={floatType}
                  onChange={(option) => handleFilterChange("floatType", option)}
                  options={floatTypeOptions}
                  styles={dropdownStyles}
                  isSearchable={false}
                  isDisabled={isFloatTypeDisabled}
                  placeholder="Float type"
                  menuPosition="absolute"
                  className="w-100"
                  classNamePrefix="react-select"
                />
              </div>
            </div>
            <div className={`col-6 col-lg-4 ${isMain && locationOptions.length > 1 ? "col-xl-1" : "col-xl-2"}`}>
              <div className="filter-control h-100 d-flex align-items-center">
                <Select
                  value={staffFilter}
                  onChange={(option) => handleFilterChange("staffFilter", option)}
                  options={staffOptions}
                  styles={dropdownStyles}
                  isSearchable={true}
                  placeholder="Staff"
                  menuPosition="absolute"
                  className="w-100"
                  classNamePrefix="react-select"
                />
              </div>
            </div>
            <div className={`col-6 col-lg-4 ${isMain && locationOptions.length > 1 ? "col-xl-1" : "col-xl-2"}`}>
              <div className="filter-control h-100 d-flex align-items-center">
                <Select
                  value={tillFilter}
                  onChange={(option) => handleFilterChange("tillFilter", option)}
                  options={tillOptions}
                  styles={dropdownStyles}
                  isSearchable={true}
                  placeholder="Till"
                  menuPosition="absolute"
                  className="w-100"
                  classNamePrefix="react-select"
                />
              </div>
            </div>
          </div>

          <div className="row g-2 g-lg-3 align-items-center">
            <div className={`col-12 ${activeTab === TABS.TRANSACTIONS ? "col-xl-4 col-lg-5" : "col-xl"}`}>
              <div className="search-input filter-control h-100 d-flex align-items-center">
                <input
                  type="text"
                  className="filter-input w-100 petty-float-report-search"
                  placeholder="Search staff, till, category, reference..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPageNumber(1);
                  }}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    border: "1px solid rgba(255, 255, 255, 0.35)",
                    color: "#333",
                    borderRadius: 8,
                  }}
                />
              </div>
            </div>
            {activeTab === TABS.TRANSACTIONS && (
              <>
                <div className="col-6 col-lg-4 col-xl-2">
                  <div className="filter-control h-100 d-flex align-items-center">
                    <Select
                      value={sortBy}
                      onChange={(val) => {
                        dispatch(setSortBy({ pageKey, sortBy: val }));
                        setPageNumber(1);
                      }}
                      options={sortByOptions}
                      styles={dropdownStyles}
                      placeholder="Sort by"
                      menuPosition="absolute"
                      className="w-100"
                      classNamePrefix="react-select"
                    />
                  </div>
                </div>
                <div className="col-6 col-lg-3 col-xl-1">
                  <div className="filter-control h-100 d-flex align-items-center w-100">
                    <div className="w-100">
                      <SortOrderDropDownSelection
                        defaultValue={sortOrder}
                        onChange={(val) => {
                          dispatch(setSortOrder({ pageKey, sortOrder: val }));
                          setPageNumber(1);
                        }}
                        disableMenuPortal
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            <div className={`col-12 ${activeTab === TABS.TRANSACTIONS ? "col-xl-auto" : "col-xl-auto"}`}>
              <div className="petty-float-report-actions d-flex align-items-center justify-content-xl-end gap-2 flex-nowrap">
                <button
                  className="clear-btn petty-float-report-action-btn flex-shrink-0"
                  onClick={clearAllFilters}
                  title="Clear all filters"
                  type="button"
                >
                  <i className="fa fa-times me-1"></i>
                  Clear filters
                </button>
                {activeTab === TABS.TRANSACTIONS && (
                  <div className="flex-shrink-0 d-flex align-items-center">
                    <ColumnSelection
                      visibleColumns={visibleColumns}
                      setVisibleColumns={(cols) => dispatch(setVisibleColumns({ pageKey, columns: cols }))}
                      columnDefinitions={columnDefinitions}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="z-report-section petty-float-kpi-section mb-4"
        style={{
          "--petty-float-theme": themeColor,
          background: isDarkMode ? "#2d3436" : "#ffffff",
          boxShadow: `0 4px 20px ${themeColor}20`,
          border: `2px solid ${themeColor}`,
        }}
      >
        <div className="petty-float-kpi-row">
          <div className={`petty-float-kpi-card${isDarkMode ? " petty-float-kpi-card-dark" : ""}`}>
            <h4>Opening balance</h4>
            <div className="petty-float-kpi-value">{formatCurrency(val(kpiData, "openingBalance", "OpeningBalance"))}</div>
          </div>
          <div className={`petty-float-kpi-card${isDarkMode ? " petty-float-kpi-card-dark" : ""}`}>
            <h4>Deposits</h4>
            <div className="petty-float-kpi-value">{formatCurrency(val(kpiData, "totalDeposits", "TotalDeposits"))}</div>
          </div>
          <div className={`petty-float-kpi-card${isDarkMode ? " petty-float-kpi-card-dark" : ""}`}>
            <h4>Withdrawals</h4>
            <div className="petty-float-kpi-value">{formatCurrency(val(kpiData, "totalWithdrawals", "TotalWithdrawals"))}</div>
          </div>
          <div className={`petty-float-kpi-card${isDarkMode ? " petty-float-kpi-card-dark" : ""}`}>
            <h4>Petty spent</h4>
            <div className="petty-float-kpi-value">{formatCurrency(val(kpiData, "pettyCashSpent", "PettyCashSpent"))}</div>
          </div>
          <div className={`petty-float-kpi-card${isDarkMode ? " petty-float-kpi-card-dark" : ""}`}>
            <h4>Closing float</h4>
            <div className="petty-float-kpi-value">{formatCurrency(val(kpiData, "closingFloat", "ClosingFloat"))}</div>
          </div>
          <div className={`petty-float-kpi-card${isDarkMode ? " petty-float-kpi-card-dark" : ""}`}>
            <h4>Transactions</h4>
            <div className="petty-float-kpi-value">{val(kpiData, "transactionCount", "TransactionCount") ?? 0}</div>
          </div>
        </div>
      </div>

      <div
        className={`card petty-float-report-data border-0 mb-4${isDarkMode ? " petty-float-report-data-dark" : ""}`}
        style={{
          "--petty-float-theme": themeColor,
          boxShadow: `0 4px 20px ${themeColor}18`,
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        <div className="petty-float-report-tabs-header">
          <ul className="nav petty-float-report-tabs">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link${activeTab === TABS.TRANSACTIONS ? " active" : ""}`}
                onClick={() => setActiveTab(TABS.TRANSACTIONS)}
              >
                <i className="fa fa-list-ul me-2"></i>
                Transactions
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link${activeTab === TABS.DAILY ? " active" : ""}`}
                onClick={() => setActiveTab(TABS.DAILY)}
              >
                <i className="fa fa-calendar-day me-2"></i>
                Daily summary
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link${activeTab === TABS.CATEGORY ? " active" : ""}`}
                onClick={() => setActiveTab(TABS.CATEGORY)}
              >
                <i className="fa fa-chart-pie me-2"></i>
                By category
              </button>
            </li>
          </ul>
          <span className="petty-float-report-record-count">
            {activeTabRecordCount} {activeTabRecordCount === 1 ? "record" : "records"}
          </span>
        </div>

        <div className="petty-float-report-table-body">
          {activeTab === TABS.TRANSACTIONS && (
            <>
              <div className="table-responsive petty-float-report-table-wrap">
                <table className="table table-hover table-sm mb-0 petty-float-report-table">
                  <thead style={tableHeadStyle}>
                    <tr>
                      {visibleColumns.pcDateTime && <th>Date / time</th>}
                      {visibleColumns.location && isMain && <th>Location</th>}
                      {visibleColumns.cashType && <th>Cash type</th>}
                      {visibleColumns.category && <th>Category</th>}
                      {visibleColumns.amount && <th className="text-end">Amount</th>}
                      {visibleColumns.staff && <th>Staff</th>}
                      {visibleColumns.till && <th>Till</th>}
                      {visibleColumns.reference && <th>Reference</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {transactionRows.length === 0 && !loading && (
                      <tr>
                        <td colSpan={visibleTransactionColCount || 1} className="petty-float-report-empty">
                          <i className="fa fa-inbox fa-2x mb-2 d-block"></i>
                          <p className="mb-0">No transactions found for the selected filters.</p>
                        </td>
                      </tr>
                    )}
                    {transactionRows.map((row) => {
                      const id = val(row, "pce_ID", "PCE_ID") ?? val(row, "pceId", "PceId");
                      return (
                        <tr key={id}>
                          {visibleColumns.pcDateTime && (
                            <td className="petty-float-col-datetime">{formatDateTime(val(row, "pcDateTime", "PCDateTime"))}</td>
                          )}
                          {visibleColumns.location && isMain && (
                            <td>{val(row, "compName", "CompName") ?? "-"}</td>
                          )}
                          {visibleColumns.cashType && (
                            <td>{renderCashTypeBadge(val(row, "cashTypeLabel", "CashTypeLabel"))}</td>
                          )}
                          {visibleColumns.category && (
                            <td className="petty-float-col-category">{val(row, "expenType", "ExpenType") ?? "-"}</td>
                          )}
                          {visibleColumns.amount && (
                            <td className="text-end petty-float-col-amount">{formatCurrency(val(row, "amount", "Amount"))}</td>
                          )}
                          {visibleColumns.staff && (
                            <td className="petty-float-col-staff" title={val(row, "staffName", "StaffName") ?? ""}>
                              {val(row, "staffName", "StaffName") ?? "-"}
                            </td>
                          )}
                          {visibleColumns.till && (
                            <td className="petty-float-col-till">{val(row, "tillName", "TillName") ?? "-"}</td>
                          )}
                          {visibleColumns.reference && (
                            <td className="petty-float-col-reference"><small>{id ?? "-"}</small></td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalPages > 0 && (
                <div className="petty-float-report-pagination">
                  <Pagination
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    setPageNumber={setPageNumber}
                    setPageSize={(opt) => {
                      setPageSize(opt);
                      setPageNumber(1);
                    }}
                    themeColor={themeColor}
                  />
                </div>
              )}
            </>
          )}

          {activeTab === TABS.DAILY && (
            <div className="table-responsive petty-float-report-table-wrap">
              <table className="table table-hover table-sm mb-0 petty-float-report-table">
                <thead style={tableHeadStyle}>
                  <tr>
                    <th>Date</th>
                    <th className="text-end">Opening</th>
                    <th className="text-end">Deposits</th>
                    <th className="text-end">Withdrawals</th>
                    <th className="text-end">Petty spent</th>
                    <th className="text-end">Closing</th>
                    <th className="text-end">Tx count</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyRows.length === 0 && !loading && (
                    <tr>
                      <td colSpan={7} className="petty-float-report-empty">
                        <i className="fa fa-calendar fa-2x mb-2 d-block"></i>
                        <p className="mb-0">No daily data for the selected filters.</p>
                      </td>
                    </tr>
                  )}
                  {dailyRows.map((row, idx) => (
                    <tr key={`${val(row, "reportDate", "ReportDate")}-${idx}`}>
                      <td className="petty-float-col-datetime">{formatDate(val(row, "reportDate", "ReportDate"))}</td>
                      <td className="text-end">{formatCurrency(val(row, "openingBalance", "OpeningBalance"))}</td>
                      <td className="text-end">{formatCurrency(val(row, "totalDeposits", "TotalDeposits"))}</td>
                      <td className="text-end">{formatCurrency(val(row, "totalWithdrawals", "TotalWithdrawals"))}</td>
                      <td className="text-end petty-float-col-amount">{formatCurrency(val(row, "pettyCashSpent", "PettyCashSpent"))}</td>
                      <td className="text-end">{formatCurrency(val(row, "closingFloat", "ClosingFloat"))}</td>
                      <td className="text-end">{val(row, "transactionCount", "TransactionCount") ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === TABS.CATEGORY && (
            <div className="table-responsive petty-float-report-table-wrap">
              <table className="table table-hover table-sm mb-0 petty-float-report-table">
                <thead style={tableHeadStyle}>
                  <tr>
                    <th>Cash type</th>
                    <th>Category</th>
                    <th className="text-end">Total amount</th>
                    <th className="text-end">Transactions</th>
                    <th className="text-end">% of type</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryRows.length === 0 && !loading && (
                    <tr>
                      <td colSpan={5} className="petty-float-report-empty">
                        <i className="fa fa-tags fa-2x mb-2 d-block"></i>
                        <p className="mb-0">No category data for the selected filters.</p>
                      </td>
                    </tr>
                  )}
                  {categoryRows.map((row, idx) => (
                    <tr key={`${val(row, "cashType", "CashType")}-${val(row, "expenType", "ExpenType")}-${idx}`}>
                      <td>{renderCashTypeBadge(val(row, "cashType", "CashType"))}</td>
                      <td className="petty-float-col-category">{val(row, "expenType", "ExpenType") ?? "-"}</td>
                      <td className="text-end petty-float-col-amount">{formatCurrency(val(row, "totalAmount", "TotalAmount"))}</td>
                      <td className="text-end">{val(row, "transactionCount", "TransactionCount") ?? 0}</td>
                      <td className="text-end">
                        {parseFloat(val(row, "percentOfTotal", "PercentOfTotal") ?? 0).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PettyFloatCashReport;
